import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../i18n/LanguageContext';
import { getIntertownRequest } from '../services/firebase';
import { ArrowLeft, CarTaxiFront, Check, ChevronRight, Clock, Copy, HelpCircle, MapPin, Share2, Star } from '../components/ReceiptIcons';
import SupportModal from '../components/SupportModal';
import RideDetailsMap from '../components/RideDetailsMap';
import { hapticSelection } from '../utils/haptics';
import { initNativeChrome } from '../utils/nativeChrome';
import { registerAndroidBackHandler } from '../utils/androidBack';
import { formatDriverName } from '../utils/driverName';
import { shareTripText } from '../utils/support';
import { formatRupees, tidyPlace } from '../utils/intertownDisplay';
import { fareRows, fareRowLabel } from '../utils/fareRows';
import { intertownReceipt, intertownRebook, intertownTimeline, timelineTimeLabels } from '../utils/intertownReceipt';
import './RideDetails.css';

/**
 * The receipt for one Intertown trip, laid out like the local ride receipt (RideDetails.js)
 * and sharing its styles and map. It has no rating, no "book again" and no payment line:
 * Intertown has no rating flow, and the request holds no payment record to show.
 */
export default function IntertownTripDetails() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { intertownRequests, userProfile } = useApp();
  const { t, lang } = useLanguage();
  const [showSupport, setShowSupport] = useState(false);
  const [fetchAttempt, setFetchAttempt] = useState(0);

  useEffect(() => { setShowSupport(false); }, [requestId]);

  useEffect(() => registerAndroidBackHandler(() => {
    if (showSupport) {
      setShowSupport(false);
      return true;
    }
    return false;
  }), [showSupport]);

  // The same white status bar as the local ride receipt (the header below is white).
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return undefined;
    let active = true;
    const apply = async () => {
      try {
        await StatusBar.setBackgroundColor({ color: '#ffffff' });
        if (active) await StatusBar.setStyle({ style: Style.Light });
      } catch { /* Status bar styling is optional on unsupported Android versions. */ }
      if (!active) await initNativeChrome();
    };
    apply();
    return () => { active = false; initNativeChrome(); };
  }, []);

  // The app keeps the latest few requests live. Any other trip is fetched once, on demand.
  const contextRequest = (intertownRequests || []).find((r) => r.id === requestId) || null;
  const hasContextRequest = Boolean(contextRequest);
  const [fetchResult, setFetchResult] = useState(null);
  const fetched = fetchResult?.id === requestId ? fetchResult : null;
  useEffect(() => {
    if (hasContextRequest || !requestId) return undefined;
    let cancelled = false;
    let timeout;
    setFetchResult({ id: requestId, status: 'loading', request: null });
    Promise.race([
      getIntertownRequest(requestId),
      new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error('Trip fetch timed out')), 15000); }),
    ])
      .then((r) => { if (!cancelled) setFetchResult({ id: requestId, status: r ? 'ready' : 'missing', request: r || null }); })
      .catch(() => { if (!cancelled) setFetchResult({ id: requestId, status: 'error', request: null }); })
      .finally(() => clearTimeout(timeout));
    return () => { cancelled = true; clearTimeout(timeout); };
  }, [requestId, hasContextRequest, fetchAttempt]);
  const request = contextRequest || fetched?.request;

  const formatDate = (ts) => {
    if (!ts) return '';
    let d = null;
    if (ts?.toDate && typeof ts.toDate === 'function') d = ts.toDate();
    else if (ts instanceof Date) d = ts;
    else if (typeof ts === 'number') d = new Date(ts);
    else if (ts?.seconds) d = new Date(ts.seconds * 1000);
    else if (typeof ts === 'string') d = new Date(ts);
    if (!d || Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(lang === 'te' ? 'te-IN' : 'en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Replace, not push: the rides list is where Android back already goes (App.js).
  const goBack = () => {
    hapticSelection();
    navigate('/rides', { replace: true });
  };
  const receiptHeader = (
    <header className="topbar ride-receipt-topbar">
      <button type="button" className="ride-receipt-icon-button" onClick={goBack} aria-label={t('common.back')}>
        <ArrowLeft size={24} aria-hidden="true" />
      </button>
      <h1>{t('details.receiptTitle')}</h1>
    </header>
  );

  if (!request) {
    const loading = !fetched || fetched.status === 'loading';
    const failed = fetched?.status === 'error';
    return (
      <div className="app ride-receipt-page">
        {receiptHeader}
        <main className="page-content ride-receipt-content">
          {loading ? <div className="ride-receipt-empty" role="status"><span className="ride-receipt-loading" aria-hidden="true" />{t('details.rideLoading')}</div> : (
            <div className="ride-receipt-empty">
              <p>{failed ? t('details.rideLoadFailed') : t('active.rideNotFound')}</p>
              <button type="button" className="ride-receipt-book-button" onClick={failed ? () => setFetchAttempt((attempt) => attempt + 1) : goBack}>
                {failed ? t('details.retry') : t('history.doneToRides')}
              </button>
            </div>
          )}
        </main>
      </div>
    );
  }

  const isCompleted = request.status === 'completed';
  // A cancelled trip is never charged, so its receipt shows no amount or fare rows.
  const isCancelled = request.status === 'cancelled';
  const receipt = intertownReceipt(request);
  // Like the local receipt, only a completed trip can be booked again.
  const rebook = isCompleted ? intertownRebook(request) : null;
  const timeline = intertownTimeline(request);
  const timeLabels = timelineTimeLabels(timeline, { locale: lang === 'te' ? 'te-IN' : 'en-IN' });
  const pickup = tidyPlace(request.pickup);
  const destination = tidyPlace(request.destination);
  const shortId = (request.id || '').slice(0, 8).toUpperCase();
  const dateText = formatDate(request.createdAt);
  const tripTypeText = t(receipt.tripType === 'return' ? 'intertown.returnTrip' : 'intertown.oneWay');
  const statusText = isCompleted || isCancelled ? t(`status.${request.status}`) : t(`intertown.status.${request.status}`);
  const totalText = receipt.total ? formatRupees(receipt.total) : '—';
  const baseLabelKey = { fixed: 'intertown.fixedFare', per_km: 'details.distanceFare' }[receipt.pricingMode] || 'fare.base';
  // What the support sheet reads about this trip.
  const supportRide = {
    id: request.id, kind: 'intertown', createdAt: request.createdAt, pickup, destination, fare: receipt.total || 0,
  };

  // The booking page applies this once, if the trip can still be booked (see intertownRebookPlan).
  const bookAgain = () => {
    hapticSelection();
    navigate('/intertown', { state: { rebookIntertown: rebook } });
  };
  const copyTripId = async () => {
    try {
      await navigator.clipboard.writeText(request.id);
      hapticSelection();
      toast.success(t('details.idCopied'));
    } catch {
      toast.error(t('details.copyFailed'));
    }
  };
  const shareReceipt = async () => {
    const lines = [`MyTownly Rides Intertown receipt #${shortId}`];
    if (dateText) lines.push(dateText);
    lines.push(`${pickup?.address || pickup?.name || 'Pickup'} → ${destination?.address || destination?.name || 'Destination'}`);
    if (request.driverName) {
      lines.push(`Driver: ${formatDriverName(request.driverName)}${request.vehicleNumber ? ` · ${request.vehicleNumber}` : ''}`);
    }
    if (isCancelled) {
      lines.push('Status: Cancelled');
    } else {
      lines.push(`Fare: ${totalText} (${tripTypeText})`);
      if (receipt.snapshot?.waitingCharge > 0) lines.push(`Includes waiting charge: ${formatRupees(receipt.snapshot.waitingCharge)} (${receipt.snapshot.waitingMinutes} min)`);
      if (receipt.billedKm != null) lines.push(`Distance: ${receipt.billedKm.toFixed(1)} km`);
      if (receipt.durationMin != null) lines.push(`Duration: ${receipt.durationMin} min`);
    }
    await shareTripText(lines.join('\n'), 'MyTownly Intertown receipt');
  };

  return (
    <div className={`app ride-receipt-page${rebook ? ' ride-receipt-with-footer' : ''}`}>
      {receiptHeader}

      <main className="page-content ride-receipt-content">
        <section className="ride-receipt-summary">
          <div className="ride-receipt-summary-heading">
            <h2>{t('intertown.pageTitle')} · {tripTypeText}</h2>
            <span className={`ride-receipt-status status-${request.status}`}>
              {isCompleted && <span className="ride-receipt-check"><Check size={12} aria-hidden="true" /></span>}
              {statusText}
            </span>
          </div>
          {dateText && <p className="ride-receipt-date">{dateText}</p>}
          {!isCancelled && <div className="ride-receipt-amount">{totalText}</div>}
        </section>

        <RideDetailsMap pickup={pickup} destination={destination} t={t} />

        <section className={`ride-receipt-route${isCancelled ? ' ride-receipt-route-end' : ''}`} aria-label={t('details.addresses')}>
          <div className="ride-receipt-stops">
            {[{ point: pickup, label: t('book.pickup'), kind: 'pickup' },
              { point: destination, label: t('details.dropoff'), kind: 'drop' }].map(({ point, label, kind }) => (
              <div className={`ride-receipt-stop ride-receipt-stop-${kind}`} key={kind}>
                <span className="ride-receipt-stop-dot" aria-hidden="true" />
                <div>
                  <span className="ride-receipt-stop-label">{label}</span>
                  <strong>{point?.name && point.name !== label ? point.name : point?.address || t('details.unavailable')}</strong>
                  {point?.address && point.address !== point.name && point?.name && point.name !== label && <p>{point.address}</p>}
                </div>
              </div>
            ))}
          </div>
          {!isCancelled && <div className="ride-receipt-metadata">
            <span><MapPin size={21} aria-hidden="true" />{receipt.billedKm != null ? `${receipt.billedKm.toFixed(1)} ${t('details.receiptKm')}` : t('details.unavailable')}</span>
            <span><Clock size={21} aria-hidden="true" />{receipt.durationMin != null ? `${receipt.durationMin} ${t('details.receiptMin')}` : t('details.unavailable')}</span>
          </div>}
          {!isCompleted && <button type="button" className="ride-receipt-action ride-receipt-quick-help" onClick={() => setShowSupport(true)}>
            <HelpCircle size={22} aria-hidden="true" /><span>{t('details.needHelp')}</span><ChevronRight size={20} aria-hidden="true" />
          </button>}
        </section>

        {(request.driverName || request.vehicleNumber || Number(request.driverRatingAvg) > 0) && <section className="ride-receipt-driver" aria-label={t('details.driverCard')}>
          <div className="ride-receipt-avatar">
            {request.driverName ? request.driverName.replace(/^(Anna|Captain)\s+/i, '').trim().charAt(0).toUpperCase() : 'A'}
          </div>
          <div className="ride-receipt-driver-info">
            <strong>{formatDriverName(request.driverName)}</strong>
            <p>{t('driver.autoFallback')}{request.vehicleNumber && ` · ${request.vehicleNumber}`}</p>
            {Number(request.driverRatingAvg) > 0 && <p className="ride-receipt-driver-rating"><Star size={13} aria-hidden="true" />{Number(request.driverRatingAvg).toFixed(1)} · {t('driver.verified')}</p>}
          </div>
        </section>}

        {/* One step alone (a request just made) tells the rider nothing, so it needs two. */}
        {timeline.length > 1 && <section className="ride-receipt-timeline" aria-labelledby="ride-receipt-timeline-title">
          <h2 id="ride-receipt-timeline-title">{t('intertown.timeline.title')}</h2>
          <ol className="ride-receipt-timeline-list">
            {timeline.map((step, index) => (
              <li className={`ride-receipt-timeline-step${step.tone === 'bad' ? ' is-bad' : ''}`} key={step.id}>
                <span className="ride-receipt-timeline-dot" aria-hidden="true" />
                <div className="ride-receipt-timeline-text">
                  <strong>{t(step.labelKey)}</strong>
                  {step.detailKey && <p>{t(step.detailKey, step.detailVars)}</p>}
                </div>
                <time dateTime={new Date(step.at).toISOString()}>
                  {timeLabels[index].time}
                  {timeLabels[index].date && <small>{timeLabels[index].date}</small>}
                </time>
              </li>
            ))}
          </ol>
        </section>}

        {!isCancelled && <section className="ride-receipt-fare" aria-labelledby="ride-receipt-fare-title">
          <h2 id="ride-receipt-fare-title">{t('details.receiptFareBreakdown')}</h2>
          <div className="ride-receipt-fare-rows">
            {receipt.snapshot && fareRows(receipt.snapshot).rows.map((row) => (
              <div className="ride-receipt-fare-row" key={row.id}>
                <div>
                  <span>{fareRowLabel(row, t, { base: baseLabelKey })}</span>
                  {row.id === 'base' && receipt.pricingMode === 'per_km' && receipt.billedKm != null && receipt.perKm != null && <p>{Math.round(receipt.billedKm * receipt.perKm) === row.amount
                    ? t('details.distanceCalculation', { km: receipt.billedKm, rate: receipt.perKm })
                    : t('details.distanceRate', { rate: receipt.perKm })}</p>}
                </div>
                <span>{formatRupees(row.amount)}</span>
              </div>
            ))}
            <div className="ride-receipt-fare-row ride-receipt-total">
              <strong>{t('fare.total')}</strong>
              <strong>{totalText}</strong>
            </div>
          </div>
          {!receipt.snapshot && <p className="ride-receipt-fare-note">{t('details.fareBreakdownUnavailable')}</p>}
        </section>}

        <section className="ride-receipt-actions" aria-label={t('details.receiptActions')}>
          <button type="button" className="ride-receipt-action" onClick={shareReceipt}><Share2 size={23} aria-hidden="true" /><span>{t('details.shareReceipt')}</span><ChevronRight size={21} aria-hidden="true" /></button>
          <button type="button" className="ride-receipt-action" onClick={() => setShowSupport(true)}><HelpCircle size={23} aria-hidden="true" /><span>{t('details.reportIssue')}</span><ChevronRight size={21} aria-hidden="true" /></button>
        </section>
        <div className="ride-receipt-reference">
          <span><strong>{t('details.rideId')}</strong> #{shortId}</span>
          <button type="button" className="ride-receipt-icon-button" aria-label={t('details.copyId')} title={t('details.copyId')} onClick={copyTripId}><Copy size={20} aria-hidden="true" /></button>
        </div>
      </main>

      {rebook && (
        <footer className="ride-receipt-footer">
          <button type="button" className="ride-receipt-book-button" onClick={bookAgain}>
            <CarTaxiFront size={22} aria-hidden="true" />{t('details.receiptRebook')}
          </button>
        </footer>
      )}

      {showSupport && (
        <SupportModal
          open={showSupport}
          onClose={() => setShowSupport(false)}
          defaultRide={supportRide}
          userProfile={userProfile}
          t={t}
        />
      )}
    </div>
  );
}
